<template>
  <div id="bio">
    <main>
      <section class="hero">
        <div class="hero-copy">
          <p class="eyebrow">Welcome 👋</p>
          <h1>Marcos Pérez Rubio</h1>
          <p class="lead">
            I build products from idea to production.
          </p>
          <p class="intro">
            I am CTO and partner at <a href="https://footanalytics.com" target="_blank" rel="noopener">Foot Analytics</a>,
            and I have been making software since 2014. I like working close to the product:
            shaping ideas, building the system, shipping it, and helping teams make better technical decisions.
          </p>
        </div>

        <aside class="profile-panel" aria-label="Profile summary">
          <transition name="fade" mode="out-in">
            <img v-bind:key="imgUrl" class="profile-img" :src="imgUrl" alt="Marcos Pérez Rubio" />
          </transition>
          <div class="profile-note">
            <span>📍 Barcelona + Madrid · 🌍</span>
            <strong>Advisory, product and engineering conversations. Selective freelance.</strong>
          </div>
        </aside>
      </section>

      <section id="work" class="section split-section">
        <div class="section-heading">
          <p class="eyebrow">What I can help with</p>
          <h2>Senior hands, founder energy.</h2>
          <p>
            Useful when a team needs someone who can build, reason about architecture, and bring senior product judgment without losing touch with the code.
          </p>
        </div>

        <div class="focus-list">
          <article class="focus-card">
            <div class="card-head">
              <span>01</span>
              <h3>Build from scratch</h3>
            </div>
            <p>MVPs, internal tools, customer-facing products and the backend foundations needed to make them real.</p>
          </article>
          <article class="focus-card">
            <div class="card-head">
              <span>02</span>
              <h3>Senior technical judgment</h3>
            </div>
            <p>Architecture reviews, roadmap pressure, team setup and pragmatic tradeoffs.</p>
          </article>
          <article class="focus-card">
            <div class="card-head">
              <span>03</span>
              <h3>Freelance &amp; advisory</h3>
            </div>
            <p>Hands-on help for product, software and AI opportunities when an outside senior perspective can unblock the work.</p>
          </article>
        </div>
      </section>

      <section id="approach" class="section approach-section">
        <div>
          <p class="eyebrow">How I work</p>
          <h2>Software without the theatre.</h2>
          <p>
            Good software work does not need mystery, inflated process or dependency games.
            I help you clarify what matters, build what is useful, and keep the work understandable as it evolves.
          </p>
        </div>
        <div class="approach-grid">
          <article>
            <span class="step-indicator">1</span>
            <h3>Plan</h3>
            <p>Make the problem smaller, clearer and testable.</p>
          </article>
          <article>
            <span class="step-indicator">2</span>
            <h3>Build</h3>
            <p>Write the thing, expose the tradeoffs and avoid cleverness for its own sake.</p>
          </article>
          <article>
            <span class="step-indicator">3</span>
            <h3>Ship</h3>
            <p>Deploy, test and learn from reality.</p>
          </article>
          <article>
            <span class="step-indicator">4</span>
            <h3>Loop back</h3>
            <p>Review what changed, what we learned and what should happen next.</p>
          </article>
        </div>
      </section>

      <section class="section stack-section" aria-label="Technology stack">
        <div class="stack-heading">
          <p class="eyebrow">Technology</p>
          <h2>Tools change. Fundamentals compound.</h2>
        </div>
        <div class="stack-copy">
          <p>
            I am comfortable across a broad stack, but especially in an AI-shaped software world,
            the important part is still the same: clear product thinking, solid architecture,
            good data, reliable systems and code that teams can own.
          </p>
          <p class="tool-belt">My main tool belt</p>
          <div class="stack-list" aria-label="Technologies I feel comfortable with">
            <span>Python</span>
            <span>TypeScript</span>
            <span>React</span>
            <span>Vue</span>
            <span>Node.js</span>
            <span>PostgreSQL</span>
            <span>Redis</span>
            <span>Kafka</span>
            <span>Docker</span>
            <span>Terraform</span>
            <span>AWS</span>
            <span>GCP</span>
            <span>Firebase</span>
            <span>LLMs</span>
            <span>Computer vision</span>
          </div>
        </div>
      </section>

      <section class="section newsletter-section">
        <div class="newsletter">
          <p>I also write a zero-spam newsletter about AI, engineering and what I am learning.</p>
          <iframe src="https://marcosperezrubio.substack.com/embed" width="360" height="280"
                  class="newsletter-frame" frameborder="0" scrolling="no" title="Marcos Pérez Rubio newsletter"></iframe>
        </div>
      </section>

      <section id="contact" class="section contact-section">
        <div class="contact-copy">
          <p class="eyebrow">Say hello</p>
          <h2>Always happy to have a chat.</h2>
          <p>
            Share an idea, ask a question, or tell me what you are building.
            I enjoy conversations around product, software, AI and the messy middle between idea and reality.
          </p>
          <a class="button primary" href="https://www.linkedin.com/in/marcospr/" target="_blank" rel="noopener">
            <font-awesome-icon :icon="['fab', 'linkedin']" />
            Talk on LinkedIn
          </a>
        </div>
        <div class="newsletter">
          <p>I also write a zero-spam newsletter about AI, engineering and what I am learning.</p>
          <iframe src="https://marcosperezrubio.substack.com/embed" width="360" height="280"
                  class="newsletter-frame" frameborder="0" scrolling="no" title="Marcos Pérez Rubio newsletter"></iframe>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <router-link :to="{name: 'Privacy'}">Privacy policy</router-link>
    </footer>
  </div>
</template>

<script>
const IMAGES = [
  '/marcos-2026.webp'
]

export default {
  name: 'bio',
  data () {
    return {
      imgUrl: IMAGES[0],
      availableImages: IMAGES,
      timeToTransition: 30 * 1000
    }
  },
  mounted () {}
}
</script>

<style lang="scss">
</style>
